import { useState, useEffect } from "react";
import { Student, Program } from "@/services/db"; 
import { format, subMonths, differenceInDays } from "date-fns";

export type FilterPeriod = "all" | "6m" | "3m" | "1m";

export interface ChartDataItem {
  name: string;
  value: number;
}

export const useReportsData = (students: Student[], filterPeriod: FilterPeriod) => {
  const [programDistribution, setProgramDistribution] = useState<ChartDataItem[]>([]);
  const [monthlyEnrollments, setMonthlyEnrollments] = useState<ChartDataItem[]>([]);
  const [feeDistribution, setFeeDistribution] = useState<ChartDataItem[]>([]);

  useEffect(() => {
    if (!Array.isArray(students)) {
      console.error('Invalid students data:', students);
      return;
    }

    try {
      // Filter students based on selected time period
      const now = new Date();
      let filteredStudents: Student[] = [...students];
      
      if (filterPeriod !== "all") {
        const monthsBack = filterPeriod === "6m" ? 6 : filterPeriod === "3m" ? 3 : 1;
        const cutoffDate = subMonths(now, monthsBack);
        
        filteredStudents = students.filter(student => {
          return student.programs.some(program => new Date(program.enrolledAt) >= cutoffDate);
        });
      }

      // Program distribution
      const programCounts: Record<string, number> = {};
      filteredStudents.forEach(student => {
        student.programs.forEach(program => {
          if (filterPeriod === "all" || new Date(program.enrolledAt) >= subMonths(now, filterPeriod === "6m" ? 6 : filterPeriod === "3m" ? 3 : 1)) {
            programCounts[program.title] = (programCounts[program.title] || 0) + 1;
          }
        });
      });

      // Sort by count in descending order and take top 5
      const programData = Object.entries(programCounts)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 5);

      setProgramDistribution(programData);

      // Monthly enrollments over the past 6 months
      const monthlyData: Record<string, number> = {};
      
      for (let i = 5; i >= 0; i--) {
        const monthDate = subMonths(now, i);
        const monthKey = format(monthDate, "MMM yyyy");
        monthlyData[monthKey] = 0;
      }

      filteredStudents.forEach(student => {
        student.programs.forEach(program => {
          const enrollDate = new Date(program.enrolledAt);
          if (differenceInDays(now, enrollDate) <= 180) { // Approximately 6 months
            const monthKey = format(enrollDate, "MMM yyyy");
            if (monthlyData[monthKey] !== undefined) {
              monthlyData[monthKey] += 1;
            }
          }
        });
      });

      setMonthlyEnrollments(
        Object.entries(monthlyData).map(([name, value]) => ({ name, value }))
      );

      // Fee distribution
      const feeRanges: Record<string, number> = {
        "0-500": 0,
        "501-1000": 0,
        "1001-1500": 0,
        "1501-2000": 0,
        "2000+": 0,
      };

      filteredStudents.forEach(student => {
        student.programs.forEach(program => {
          if (filterPeriod === "all" || new Date(program.enrolledAt) >= subMonths(now, filterPeriod === "6m" ? 6 : filterPeriod === "3m" ? 3 : 1)) {
            const fee = program.fee;
            if (fee <= 500) feeRanges["0-500"]++;
            else if (fee <= 1000) feeRanges["501-1000"]++;
            else if (fee <= 1500) feeRanges["1001-1500"]++;
            else if (fee <= 2000) feeRanges["1501-2000"]++;
            else feeRanges["2000+"]++;
          }
        });
      });

      setFeeDistribution(
        Object.entries(feeRanges)
          .map(([name, value]) => ({ name, value }))
          .filter(item => item.value > 0)
      );
    } catch (error) {
      console.error('Error processing report data:', error);
      setProgramDistribution([]);
      setMonthlyEnrollments([]);
      setFeeDistribution([]);
    }
  }, [students, filterPeriod]);

  return {
    programDistribution,
    monthlyEnrollments,
    feeDistribution
  };
};
